import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { setHead } from "../lib/head";
import { useSite } from "../context/SiteContext";
import { SectionHead } from "../components/site/Brand";
import { GoldDatePicker } from "../components/site/GoldDatePicker";
import { BlogPager } from "../components/site/BlogPager";
import { BlogFilters } from "../components/site/BlogFilters";
import { FeaturedPost } from "../components/site/FeaturedPost";
import { PopularThisWeek, readTime } from "../components/site/PopularThisWeek";

export const PostCard = ({ p, cdn, lazy }) => (
  <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
    className="glass-card group flex flex-col overflow-hidden transition-transform hover:-translate-y-1">
    <div className="aspect-[16/9] overflow-hidden bg-[#12141C]">
      {p.featured_image && (
        <img src={fileUrl(p.featured_image, cdn)} alt={p.featured_alt || p.title} loading={lazy ? "lazy" : "eager"}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      )}
    </div>
    <div className="flex flex-1 flex-col p-6">
      <p className="text-xs uppercase tracking-widest text-[#D4AF37]">{p.category && p.category !== "general" ? `${p.category} · ` : ""}{new Date(p.published_at || p.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} · <span data-testid={`read-time-${p.slug}`}>{readTime(p)}</span></p>
      <h3 className="mt-3 font-serif text-lg font-semibold text-white">{p.title}</h3>
      <p className="mt-2 line-clamp-3 text-sm text-zinc-400">{p.excerpt || p.seo?.meta_description}</p>
      <span className="gold-underline mt-auto w-fit pt-5 text-xs uppercase tracking-widest text-white">Read more</span>
    </div>
  </Link>
);

const PER_PAGE = 12;

export default function BlogList() {
  const { site } = useSite();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, parseInt(params.get("page"), 10) || 1);
  const cat = ["cricket", "casino"].includes(params.get("cat")) ? params.get("cat") : "";
  const q = params.get("q") || "";
  const [data, setData] = useState(null);
  const [featured, setFeatured] = useState(undefined);
  const [range, setRange] = useState({ start: "", end: "" });
  const perf = site?.performance;
  const posts = data?.items ?? null;
  const plain = !cat && !q && !range.start && !range.end;
  const showFeatured = plain && page === 1 && featured;

  useEffect(() => {
    const qs = new URLSearchParams({ ...(cat && { cat }), ...(page > 1 && { page: String(page) }) }).toString();
    const label = cat ? `${cat[0].toUpperCase()}${cat.slice(1)} ` : "";
    setHead({ title: `${label}Blog${page > 1 ? ` – Page ${page}` : ""} | DaddyAnna247 — Cricket & Casino Tips`, description: "Guides, cricket betting tips, casino strategies and news from DaddyAnna247.", canonical: `${window.location.origin}/blog${qs ? `?${qs}` : ""}` });
  }, [page, cat]);

  useEffect(() => {
    api.get("/public/posts/featured").then(({ data: d }) => setFeatured(d.post)).catch(() => setFeatured(null));
  }, []);

  useEffect(() => {
    if (featured === undefined) return undefined;
    let live = true;
    const qs = new URLSearchParams({ kind: "blog", limit: String(PER_PAGE), page: String(page), ...(cat && { category: cat }), ...(q && { q }),
      ...(plain && featured && { exclude: featured.id }), ...(range.start && { start: range.start }), ...(range.end && { end: range.end }) });
    api.get(`/public/posts?${qs}`).then(({ data: d }) => live && setData(d)).catch(() => live && setData({ items: [], total: 0, page: 1, pages: 1 }));
    return () => { live = false; };
  }, [range, page, cat, q, featured, plain]);

  const update = (next) => {
    const merged = { cat, q, page: 1, ...next };
    setParams(Object.fromEntries(Object.entries({ cat: merged.cat, q: merged.q, page: merged.page > 1 ? String(merged.page) : "" }).filter(([, v]) => v)));
  };
  const goPage = (n) => { update({ page: n }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const changeRange = (r) => { setRange(r); if (page !== 1) update({}); };
  const empty = q ? `No articles found for “${q}”.` : range.start || range.end ? "No articles in this date range." : cat ? `No ${cat} articles yet.` : "No articles yet. Check back soon.";

  return (
    <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" data-testid="blog-list-page">
      <SectionHead eyebrow="Blog" title="Insights & Guides" sub="Tips, strategies and updates for smarter, responsible play." />
      <BlogFilters cat={cat} q={q} onCat={(c) => update({ cat: c })} onSearch={(s) => update({ q: s })} />
      <div className="mb-10 flex flex-wrap items-center gap-3">
        <span className="text-xs uppercase tracking-widest text-zinc-500">Filter by date</span>
        <GoldDatePicker start={range.start} end={range.end} onChange={changeRange} testid="blog-date" />
        {data && !plain && <span className="text-xs text-zinc-500" data-testid="blog-result-count">{data.total} result{data.total === 1 ? "" : "s"}</span>}
      </div>
      {showFeatured && <FeaturedPost p={featured} cdn={perf?.cdn_base_url} />}
      {plain && page === 1 && <PopularThisWeek />}
      {posts === null ? (
        <p className="text-zinc-500">Loading…</p>
      ) : posts.length === 0 && !showFeatured ? (
        <p className="text-zinc-500" data-testid="blog-empty">{empty}</p>
      ) : (
        <>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
            {posts.map((p) => <PostCard key={p.id} p={p} cdn={perf?.cdn_base_url} lazy={perf?.lazyload !== false} />)}
          </div>
          <BlogPager page={data.page} pages={data.pages} total={data.total + (plain && featured ? 1 : 0)} onPage={goPage} />
        </>
      )}
    </section>
  );
}
